<template>
  <div class="create-event-container">
    <h4>{{ isEditMode ? 'Editează Evenimentul' : 'Creează un Eveniment Nou' }}</h4>
    
    <form @submit.prevent="handleSubmit">
      <div class="form-group">
        <label for="title">Titlu:</label>
        <input type="text" id="title" v-model="event.title" required />
      </div>
      
      <div class="form-group">
        <label for="description">Descriere:</label>
        <textarea id="description" v-model="event.description" required></textarea>
      </div>
      
      <div class="form-group">
        <label for="start_time">Data Început:</label>
        <input type="datetime-local" id="start_time" v-model="event.start_time" required />
      </div>
      
      <div class="form-group">
        <label for="end_time">Data Sfârșit:</label>
        <input type="datetime-local" id="end_time" v-model="event.end_time" required />
      </div>
      
      <div class="form-group">
        <label for="location">Locație:</label>
        <input type="text" id="location" v-model="event.location" required />
      </div>
      
      <div class="form-group">
        <label for="duration_hours">Ore Voluntariat (ex: 2.5):</label>
        <input type="number" step="0.1" id="duration_hours" v-model="event.duration_hours" required />
      </div>

      <div class="form-group">
        <label for="confirmation_code">Cod Confirmare Prezență:</label>
        <input type="text" id="confirmation_code" v-model="event.confirmation_code" required />
      </div>

      <div class="button-group">
        <button type="submit" :disabled="loading">
          {{ loading ? 'Se salvează...' : (isEditMode ? 'Salvează Modificările' : 'Creează Eveniment') }}
        </button>
        <button v-if="isEditMode" @click="handleCancelEdit" type="button" class="cancel-btn">
          Anulează Editarea
        </button>
      </div>
      
      <p v-if="message" :class="{ 'success-message': isSuccess, 'error-message': !isSuccess }">
        {{ message }}
      </p>
    </form>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits, computed } from 'vue'; // 3. Am adăugat 'watch', 'defineProps', 'computed'
import apiClient from '@/api.js';

// 4. Definim ce "props" (date) primim de la părinte
const props = defineProps({
  eventData: Object // Așteptăm un obiect eventData, care poate fi null
});

// 5. Definim ce evenimente putem emite către părinte
const emit = defineEmits(['operation-complete']);

// 6. Obiectul formularului
const event = ref({
  id: null,
  title: '',
  description: '',
  start_time: '',
  end_time: '',
  location: '',
  duration_hours: null,
  confirmation_code: ''
});

// Starea de feedback
const loading = ref(false);
const message = ref('');
const isSuccess = ref(false);

// 7. O variabilă "calculată" care ne spune dacă suntem în modul editare
const isEditMode = computed(() => !!event.value.id);

// 8. O funcție de resetare a formularului
const resetForm = () => {
  event.value = { 
    id: null, title: '', description: '', start_time: '', 
    end_time: '', location: '', duration_hours: null, confirmation_code: '' 
  };
  message.value = '';
  isSuccess.value = false;
};

// 9. Funcția de formatare a datei din formatul bazei de date (ISO)
//    în formatul cerut de input-ul <input type="datetime-local"> (care e fără 'Z' și milisecunde)
const formatDateTimeForInput = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  // Extragem componentele și le formatăm (YYYY-MM-DDTHH:mm)
  const YYYY = date.getFullYear();
  const MM = (date.getMonth() + 1).toString().padStart(2, '0');
  const DD = date.getDate().toString().padStart(2, '0');
  const HH = date.getHours().toString().padStart(2, '0');
  const mm = date.getMinutes().toString().padStart(2, '0');
  return `${YYYY}-${MM}-${DD}T${HH}:${mm}`;
};

// 10. FOLOSIM 'watch' PENTRU A REACȚIONA CÂND SE DĂ CLIC PE "EDITEAZĂ"
// Aceasta este piesa centrală: monitorizează prop-ul 'eventData'
watch(() => props.eventData, (newEventData) => {
  if (newEventData) {
    // Dacă am primit date, populăm formularul
    console.log('CreateEvent a primit date pentru editare:', newEventData);
    event.value.id = newEventData.id;
    event.value.title = newEventData.title;
    event.value.description = newEventData.description;
    event.value.location = newEventData.location;
    event.value.duration_hours = newEventData.duration_hours;
    event.value.confirmation_code = newEventData.confirmation_code;
    // Formatăm datele pentru input
    event.value.start_time = formatDateTimeForInput(newEventData.start_time);
    event.value.end_time = formatDateTimeForInput(newEventData.end_time);
    
  } else {
    // Dacă am primit null (ex. după anulare), resetăm formularul
    resetForm();
  }
});

// 11. Funcția de ANULARE (emite 'operation-complete' pentru a reseta părintele)
const handleCancelEdit = () => {
  resetForm();
  emit('operation-complete'); // Anunțăm părintele (AdminPanel)
};

// 12. Funcția de SUBMIT (acum e "smart")
const handleSubmit = async () => {
  loading.value = true;
  message.value = '';
  isSuccess.value = false;

  // Pregătim datele de trimis
  const payload = {
    title: event.value.title,
    description: event.value.description,
    start_time: event.value.start_time, // Input-ul datetime-local e deja în formatul corect
    end_time: event.value.end_time,
    location: event.value.location,
    duration_hours: parseFloat(event.value.duration_hours),
    confirmation_code: event.value.confirmation_code
  };

  try {
    if (isEditMode.value) {
      // --- MODUL EDITARE (PUT) ---
      const response = await apiClient.put(`/events/${event.value.id}`, payload);
      message.value = `Evenimentul "${response.data.title}" a fost actualizat!`;
      isSuccess.value = true;
      // Nu resetăm formularul, dar anunțăm părintele
      emit('operation-complete'); // <-- APELEAZĂ EMIT AICI
      
    } else {
      // --- MODUL CREARE (POST) ---
      const response = await apiClient.post('/events', payload);
      message.value = `Evenimentul "${response.data.title}" a fost creat cu succes!`;
      isSuccess.value = true;
      resetForm(); // Resetăm formularul
      emit('operation-complete'); // <-- APELEAZĂ EMIT ȘI AICI
    }

  } catch (error) {
    // ... (codul de eroare rămâne la fel)
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.create-event-container {
  border: 1px solid #ddd; 
  padding: 25px;
  border-radius: 10px; 
  background-color: #FFFFFF; 
  margin-top: 25px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05); /* Umbră subtilă */
}
h4 {
    color: #1C748C; /* Titlu formular */
    margin-top: 0;
    margin-bottom: 20px;
    border-bottom: 1px solid #eee;
    padding-bottom: 10px;
}
.form-group { 
    margin-bottom: 15px; 
}
.form-group label { 
    display: block; 
    margin-bottom: 5px; 
    font-weight: bold; 
    color: #444;
    font-size: 0.95em;
}
.form-group input,
.form-group textarea {
  width: 100%; 
  padding: 10px; 
  box-sizing: border-box;
  border: 1px solid #ccc; 
  border-radius: 6px; /* Butoane mai rotunde */
  background-color: #fcfcfc;
  font-size: 1em;
}

/* NOU: Layout pe coloane */
.form-group:nth-child(6), /* Ore Voluntariat */
.form-group:nth-child(7) { /* Cod Confirmare */
    width: 48%; /* Ocupă jumătate de spațiu */
    display: inline-block; /* Aliniere pe aceeași linie */
    margin-right: 1%;
}

.button-group {
  display: flex;
  gap: 15px;
  margin-top: 25px;
}
button {
  background-color: #1C748C; /* Albastru Primar Deschis */
  color: white;
  padding: 12px 20px; 
  border: none;
  border-radius: 6px; 
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s;
}
button:disabled { 
    background-color: #ccc; 
    cursor: not-allowed; 
}
.cancel-btn {
  background-color: #6c757d; /* Gri */
}
.success-message { color: #27ae60; margin-top: 15px; font-weight: bold; }
.error-message { color: #C0392B; margin-top: 15px; font-weight: bold; }

/* ########################################## */
/* ## STILURI PENTRU FORMULAR PE MOBIL (NOU)
/* ########################################## */
@media (max-width: 768px) {
  /* Țintim aceleași câmpuri */
  .form-group:nth-child(6),
  .form-group:nth-child(7) {
    width: 100%; /* Pe mobil, le forțăm să ocupe toată lățimea */
    display: block; /* Asigurăm că sunt elemente de tip bloc */
    margin-right: 0; /* Scoatem marginea */
  }
}
</style>